import { Injectable } from '@angular/core';
import {
  ColumnConfig,
  COLUMN_TYPE,
  isDataWithCountColumnConfig,
  isDateFormatColumnConfig,
  isStatusColumnConfig,
  isTextWithSubColumnConfig,
  isDataWithCountFieldColumnConfig,
  isDataCodeColumnConfig,
} from '@d-components/table/table.interface';
import { DEFAULT_FORMAT_DATETIME, SystemStatus } from '@d-constants';
import { TranslateService } from '@ngx-translate/core';

// to disable warnings then we have a config in angular.json allowedCommonJsDependencies
import dayjs from 'dayjs';
import FileSaver from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { isObject } from 'lodash-es';
import * as xlsx from 'xlsx';

@Injectable({ providedIn: 'root' })
export class TableExportService {
  constructor(private translateService: TranslateService) {}

  exportPdfFromDataTable<T>(
    columnConfig: any[],
    data: T[],
    fileName: string = 'List.pdf',
    fileTitle: string = '',
  ) {
    const doc = new jsPDF('landscape');
    doc.text(fileTitle ? fileTitle : fileName + ' Exported List', 5, 5);
    const header = this.getHeader(columnConfig);
    const headerTranslated = header.map((head) => {
      const config = columnConfig.find((item) => item.columnName === head);
      if (config && config.headerCurrency) {
        return (
          this.translateService.instant(head) + `(${config.headerCurrency})`
        );
      }
      return this.translateService.instant(head);
    });

    autoTable(doc, {
      headStyles: {
        fillColor: '#009390',
        minCellWidth: Math.ceil(240 / headerTranslated.length),
      },
      bodyStyles: { overflow: 'linebreak' },
      head: [headerTranslated],
      body: this.formatPDFBodyDataWithColumn(columnConfig, data),
    });
    doc.save(`${fileName} List-${dayjs().format('YYYYMMDD')}.pdf`);
  }

  exportCSVFromDataTable(
    columnConfig: ColumnConfig<any>[],
    data: any[],
    fileName: string = 'List.csv',
  ) {
    const header = this.getHeader(columnConfig);
    const headerTranslated = header.map((head) => {
      const config = columnConfig.find((item) => item.columnName === head);
      if (config && config.headerCurrency) {
        return (
          this.translateService.instant(head) + `(${config.headerCurrency})`
        );
      }
      return this.translateService.instant(head);
    });
    const json = [
      headerTranslated,
      ...this.formatCSVDataWithColumn(columnConfig, data),
    ];
    const worksheet = xlsx.utils.aoa_to_sheet(json);
    const csv = xlsx.utils.sheet_to_csv(worksheet);

    const file: Blob = new Blob([csv], { type: 'text/plain;charset=UTF-8' });
    FileSaver.saveAs(
      file,
      `${fileName} List-${dayjs().format('YYYYMMDD')}.csv`,
    );
  }

  exportExcelFromDataTable(
    columnConfig: ColumnConfig<any>[],
    data: any[],
    fileName: string = 'List.xlsx',
  ) {
    const header = this.getHeader(columnConfig);
    const headerTranslated = header.map((head) => {
      const config = columnConfig.find((item) => item.columnName === head);
      if (config && config.headerCurrency) {
        return (
          this.translateService.instant(head) + `(${config.headerCurrency})`
        );
      }
      return this.translateService.instant(head);
    });
    const json = [
      headerTranslated,
      ...this.formatExcelDataWithColumn(columnConfig, data),
    ];
    const worksheet = xlsx.utils.aoa_to_sheet(json);
    const workbook = xlsx.utils.book_new();
    xlsx.utils.book_append_sheet(workbook, worksheet, 'List');
    const excelBuffer: any = xlsx.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });

    const file: Blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
    });
    FileSaver.saveAs(
      file,
      `${fileName} List-${dayjs().format('YYYYMMDD')}.xlsx`,
    );
  }

  private getHeader(columnConfig: ColumnConfig<any>[]): string[] {
    return <any[]>(
      columnConfig
        .filter(
          (i) =>
            ![COLUMN_TYPE.MoreAction, COLUMN_TYPE.ListActionIcon].includes(
              <COLUMN_TYPE>i.type,
            ),
        )
        .map((i) =>
          i.type === COLUMN_TYPE.TextWithSub && i['subColumnName']
            ? i.columnName + i['subColumnName']
            : i.columnName,
        )
    );
  }

  private formatPDFBodyDataWithColumn(
    columnsConfig: ColumnConfig<any>[],
    data: any[],
  ): any[] {
    return data.map((dataItem) =>
      columnsConfig.map((val) => {
        const config = <ColumnConfig<any>>(
          columnsConfig.find((column) => column.columnName === val.columnName)
        );

        const dataByDataField = this.getDataByPath(
          dataItem,
          <string>config.dataField,
        );

        if (isDateFormatColumnConfig(config)) {
          if (!dataByDataField) return null;
          if (typeof dataByDataField === 'number')
            return dayjs(dataByDataField).format(config.format || DEFAULT_FORMAT_DATETIME);
          return dayjs(`${dataByDataField}Z`).format(config.format || DEFAULT_FORMAT_DATETIME);
        }

        if (isStatusColumnConfig(config)) {
          return this.getStatus(config.statusOptions, dataByDataField);
        }

        if (isDataCodeColumnConfig(config)) {
          return this.getDataByCode(config.options, dataByDataField);
        }

        if (isTextWithSubColumnConfig(config)) {
          const subfield = this.getDataByPath(
            dataItem,
            <string>config.subDataField,
          );
          return dataByDataField && subfield
            ? dataByDataField + '; ' + subfield
            : dataByDataField + '\n ' + subfield;
        }

        if (isDataWithCountColumnConfig(config)) {
          return this.getDataDisplayWithCount(
            dataItem,
            <string>config.dataField,
            <string>config.displayField,
            config.separatorCharacter,
          );
        }

        if (isDataWithCountFieldColumnConfig(config)) {
          return this.getDataDisplayWithCountField(
            dataItem,
            <string>config.dataField,
            <string>config.countNoField,
          );
        }

        return dataByDataField;
      }),
    );
  }

  getDataDisplayWithCount(
    data: any,
    dataField: string,
    showField: string,
    separatorCharacter = ', ',
  ) {
    const dataItem = this.getDataByPath(data, dataField);
    return Array.isArray(dataItem)
      ? dataItem
          .map((i: any) => (isObject(i) ? i[showField] : i))
          .join(separatorCharacter) + `  (${dataItem.length || 0})`
      : dataItem + `  (0)`;
  }

  getDataDisplayWithCountField(
    data: any,
    dataField: string,
    showField: string,
  ) {
    const dataItem = this.getDataByPath(data, dataField);
    const countField = this.getDataByPath(data, showField);
    return Array.isArray(dataItem)
      ? dataItem.map((i: any) => i[showField]).join(', ') +
          `  (${countField || 0})`
      : dataItem + `  (${countField || 0})`;
  }

  private formatExcelDataWithColumn(
    columnsConfig: ColumnConfig<any>[],
    data: any[],
  ): any[] {
    return data.map((dataItem) =>
      columnsConfig.map((val) => {
        const config = <ColumnConfig<any>>(
          columnsConfig.find((column) => column.columnName === val.columnName)
        );

        const dataByDataField = this.getDataByPath(
          dataItem,
          <string>config.dataField,
        );

        if (isDateFormatColumnConfig(config)) {
          if (!dataByDataField) return null;
          if (typeof dataByDataField === 'number')
            return dayjs(dataByDataField).format(config.format || DEFAULT_FORMAT_DATETIME);
          return dayjs(`${dataByDataField}Z`).format(config.format || DEFAULT_FORMAT_DATETIME);
        }

        if (isStatusColumnConfig(config)) {
          return this.getStatus(config.statusOptions, dataByDataField);
        }

        if (isDataCodeColumnConfig(config)) {
          return this.getDataByCode(config.options, dataByDataField);
        }

        if (isTextWithSubColumnConfig(config)) {
          return dataByDataField &&
            this.getDataByPath(dataItem, <string>config.subDataField)
            ? dataByDataField +
                '; \n' +
                this.getDataByPath(dataItem, <string>config.subDataField)
            : dataByDataField +
                '\n ' +
                this.getDataByPath(dataItem, <string>config.subDataField);
        }

        if (isDataWithCountColumnConfig(config)) {
          return this.getDataDisplayWithCount(
            dataItem,
            <string>config.dataField,
            <string>config.displayField,
            config.separatorCharacter,
          );
        }

        if (isDataWithCountFieldColumnConfig(config)) {
          return this.getDataDisplayWithCountField(
            dataItem,
            <string>config.dataField,
            <string>config.countNoField,
          );
        }

        return Array.isArray(dataByDataField)
          ? dataByDataField.join(', ')
          : dataByDataField;
      }),
    );
  }

  private formatCSVDataWithColumn(
    columnsConfig: ColumnConfig<any>[],
    data: any[],
  ): any[] {
    return data.map((dataItem) =>
      columnsConfig.map((val) => {
        const config = <ColumnConfig<any>>(
          columnsConfig.find((column) => column.columnName === val.columnName)
        );

        const dataByDataField = this.getDataByPath(
          dataItem,
          <string>config.dataField,
        );

        if (isDateFormatColumnConfig(config)) {
          if (!dataByDataField) return null;
          if (typeof dataByDataField === 'number')
            return dayjs(dataByDataField).format(config.format || DEFAULT_FORMAT_DATETIME);
          return dayjs(`${dataByDataField}Z`).format(config.format || DEFAULT_FORMAT_DATETIME);
        }

        if (isStatusColumnConfig(config)) {
          return this.getStatus(config.statusOptions, dataByDataField);
        }

        if (isDataCodeColumnConfig(config)) {
          return this.getDataByCode(config.options, dataByDataField);
        }

        if (isTextWithSubColumnConfig(config)) {
          return dataByDataField &&
            this.getDataByPath(dataItem, <string>config.subDataField)
            ? dataByDataField +
                '; ' +
                this.getDataByPath(dataItem, <string>config.subDataField)
            : dataByDataField +
                this.getDataByPath(dataItem, <string>config.subDataField);
        }

        if (isDataWithCountColumnConfig(config)) {
          return this.getDataDisplayWithCount(
            dataItem,
            <string>config.dataField,
            <string>config.displayField,
            config.separatorCharacter,
          );
        }

        if (isDataWithCountFieldColumnConfig(config)) {
          return this.getDataDisplayWithCountField(
            dataItem,
            <string>config.dataField,
            <string>config.countNoField,
          );
        }

        return Array.isArray(dataByDataField)
          ? dataByDataField.join(', ')
          : dataByDataField;
      }),
    );
  }

  private getDataByPath(data: any, path: string = '') {
    if (path && path.includes(',')) return 'Invalid path';
    const pathArr = path?.replace(/[\[\]\.]+/gi, ',').split(',');
    let rs = data;
    pathArr.forEach((i: string) => {
      rs = rs[i] ?? rs;
    });
    return typeof rs === 'object' && !Array.isArray(rs) ? '' : rs;
  }

  private getStatus(statusOptions, value) {
    return statusOptions?.find((i) => i.value === value)
      ? this.translateService.instant(
          <string>statusOptions?.find((i) => i.value === value)?.label ||
            <string>SystemStatus.find((i) => i.value === value)?.label,
        )
      : '';
  }

  private getDataByCode(options, value) {
    return options?.find((i) => i.value === value)
      ? this.translateService.instant(
          <string>options?.find((i) => i.value === value)?.label,
        )
      : '';
  }
}
